import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Collapse, Divider,
  IconButton, TextField, Tooltip, Typography, useTheme,
} from '@mui/material';
import {
  AutoAwesome as GenerateIcon,
  Check as ApproveIcon,
  Close as RejectIcon,
  Edit as EditIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { RuleSuggestion } from '../../types';
import { PlaybooksService } from '../../services/playbooksService';
import { colorUtils } from '../../theme';

const CONFIDENCE_COLOR: Record<string, string> = {
  high: 'error',
  medium: 'warning',
  low: 'default',
};

const STATUS_COLOR: Record<RuleSuggestion['status'], 'default' | 'success' | 'error'> = {
  pending: 'default',
  approved: 'success',
  rejected: 'error',
};

interface RuleSuggestionsPanelProps {
  templateId: string;
  clientId: string;
}

const RuleCard: React.FC<{
  suggestion: RuleSuggestion;
  onApprove: (id: string, ruleJson?: object) => Promise<void>;
  onReject: (id: string) => Promise<void>;
  onEdit: (id: string, ruleJson: object) => Promise<void>;
}> = ({ suggestion, onApprove, onReject, onEdit }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editJson, setEditJson] = useState('');
  const [editError, setEditError] = useState('');
  const [loading, setLoading] = useState(false);
  const rule = suggestion.rule_json;

  const handleApprove = async () => {
    setLoading(true);
    try { await onApprove(suggestion.id); } finally { setLoading(false); }
  };

  const handleReject = async () => {
    setLoading(true);
    try { await onReject(suggestion.id); } finally { setLoading(false); }
  };

  const handleStartEdit = () => {
    setEditJson(JSON.stringify(suggestion.rule_json, null, 2));
    setEditError('');
    setEditing(true);
    setExpanded(true);
  };

  const handleSaveEdit = async () => {
    try {
      const parsed = JSON.parse(editJson);
      setLoading(true);
      await onEdit(suggestion.id, parsed);
      setEditing(false);
    } catch {
      setEditError('Invalid JSON — fix before saving');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveEdited = async () => {
    try {
      const parsed = JSON.parse(editJson);
      setLoading(true);
      await onApprove(suggestion.id, parsed);
      setEditing(false);
    } catch {
      setEditError('Invalid JSON — fix before approving');
    } finally {
      setLoading(false);
    }
  };

  const isPending = suggestion.status === 'pending';

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.border.main}`,
        borderRadius: 1.5,
        overflow: 'hidden',
        opacity: suggestion.status !== 'pending' ? 0.65 : 1,
        transition: 'opacity 0.2s',
      }}
    >
      {/* Header row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1.25,
          backgroundColor: theme.palette.background.tertiary,
          cursor: 'pointer',
        }}
        onClick={() => setExpanded((p) => !p)}
      >
        <Chip
          label={rule.confidence}
          size="small"
          color={CONFIDENCE_COLOR[rule.confidence] as any}
          sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }}
        />
        <Typography sx={{ flex: 1, fontSize: '0.82rem', fontWeight: 600 }}>
          {rule.name}
        </Typography>
        <Chip
          label={suggestion.status}
          size="small"
          color={STATUS_COLOR[suggestion.status]}
          sx={{ height: 18, fontSize: '0.6rem' }}
        />
        {expanded ? <CollapseIcon sx={{ fontSize: 16 }} /> : <ExpandIcon sx={{ fontSize: 16 }} />}
      </Box>

      {/* Expanded body */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {editing ? (
            <>
              <TextField
                multiline
                rows={12}
                fullWidth
                value={editJson}
                onChange={(e) => { setEditJson(e.target.value); setEditError(''); }}
                error={!!editError}
                helperText={editError || 'Edit the rule JSON directly'}
                sx={{
                  '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.78rem' },
                }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="contained" color="success" onClick={handleApproveEdited} disabled={loading}>
                  Approve edited
                </Button>
                <Button size="small" variant="outlined" onClick={handleSaveEdit} disabled={loading}>
                  Save draft
                </Button>
                <Button size="small" onClick={() => setEditing(false)} disabled={loading}>
                  Cancel
                </Button>
              </Box>
            </>
          ) : (
            <>
              {/* Conditions */}
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Conditions
                </Typography>
                <Box sx={{ mt: 0.5, display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                  {Object.entries(rule.conditions).map(([k, v]) => (
                    <Box key={k} sx={{ display: 'flex', gap: 1 }}>
                      <Typography sx={{ fontSize: '0.75rem', fontFamily: 'monospace', color: theme.palette.info.light }}>
                        {k}
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: theme.palette.text.secondary }}>
                        {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>

              {/* Actions */}
              <Box>
                <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Actions
                </Typography>
                <Box component="ul" sx={{ mt: 0.5, pl: 2, mb: 0 }}>
                  {rule.actions.map((a, i) => (
                    <Box component="li" key={i} sx={{ fontSize: '0.78rem', color: theme.palette.text.secondary, mb: 0.3 }}>
                      {a}
                    </Box>
                  ))}
                </Box>
              </Box>
            </>
          )}
        </Box>

        {/* Action buttons */}
        {isPending && !editing && (
          <>
            <Divider sx={{ borderColor: theme.palette.border.light }} />
            <Box sx={{ display: 'flex', gap: 1, px: 2, py: 1 }}>
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={loading ? <CircularProgress size={12} color="inherit" /> : <ApproveIcon sx={{ fontSize: 14 }} />}
                onClick={handleApprove}
                disabled={loading}
                sx={{ fontSize: '0.72rem', height: 28 }}
              >
                Approve
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                onClick={handleStartEdit}
                disabled={loading}
                sx={{ fontSize: '0.72rem', height: 28 }}
              >
                Edit
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<RejectIcon sx={{ fontSize: 14 }} />}
                onClick={handleReject}
                disabled={loading}
                sx={{ fontSize: '0.72rem', height: 28 }}
              >
                Reject
              </Button>
            </Box>
          </>
        )}
      </Collapse>
    </Box>
  );
};

const RuleSuggestionsPanel: React.FC<RuleSuggestionsPanelProps> = ({ templateId, clientId }) => {
  const theme = useTheme();
  const [suggestions, setSuggestions] = useState<RuleSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = async () => {
    try {
      const res = await PlaybooksService.getRuleSuggestions(templateId, clientId);
      setSuggestions(res.suggestions);
    } catch { /* silent */ }
  };

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [templateId, clientId]);

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    let ticks = 0;
    pollRef.current = setInterval(async () => {
      await load();
      ticks++;
      if (ticks >= 10 && pollRef.current) {
        clearInterval(pollRef.current);
        setGenerating(false);
      }
    }, 3000);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await PlaybooksService.generateRules(templateId, clientId);
      startPolling();
    } catch {
      setGenerating(false);
    }
  };

  const handleApprove = async (id: string, ruleJson?: object) => {
    const updated = await PlaybooksService.approveRuleSuggestion(id, ruleJson);
    setSuggestions((prev) => prev.map((s) => s.id === id ? updated : s));
  };

  const handleReject = async (id: string) => {
    const updated = await PlaybooksService.rejectRuleSuggestion(id);
    setSuggestions((prev) => prev.map((s) => s.id === id ? updated : s));
  };

  const handleEdit = async (id: string, ruleJson: object) => {
    const updated = await PlaybooksService.editRuleSuggestion(id, ruleJson);
    setSuggestions((prev) => prev.map((s) => s.id === id ? updated : s));
  };

  const pending = suggestions.filter((s) => s.status === 'pending');
  const approved = suggestions.filter((s) => s.status === 'approved');
  const rejected = suggestions.filter((s) => s.status === 'rejected');

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: theme.palette.text.primary }}>
            AI-Suggested Detection Rules
          </Typography>
          <Typography variant="caption" sx={{ color: theme.palette.text.tertiary }}>
            Rules generated from your playbook steps — review before they go live
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => load()} sx={{ color: theme.palette.text.secondary }}>
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant="contained"
            startIcon={generating ? <CircularProgress size={13} color="inherit" /> : <GenerateIcon sx={{ fontSize: 15 }} />}
            onClick={handleGenerate}
            disabled={generating}
            sx={{ fontSize: '0.75rem', height: 32 }}
          >
            {generating ? 'Generating…' : 'Generate Rules'}
          </Button>
        </Box>
      </Box>

      {loading && suggestions.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={24} />
        </Box>
      ) : suggestions.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center', py: 4, px: 2,
            border: `1px dashed ${theme.palette.border.main}`,
            borderRadius: 1.5,
          }}
        >
          <GenerateIcon sx={{ fontSize: 32, color: theme.palette.text.disabled, mb: 1 }} />
          <Typography variant="body2" sx={{ color: theme.palette.text.tertiary }}>
            No suggestions yet. Click "Generate Rules" to let the AI analyse your playbook steps.
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* Pending */}
          {pending.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Pending review ({pending.length})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {pending.map((s) => (
                  <RuleCard key={s.id} suggestion={s} onApprove={handleApprove} onReject={handleReject} onEdit={handleEdit} />
                ))}
              </Box>
            </Box>
          )}

          {/* Approved */}
          {approved.length > 0 && (
            <Box sx={{ mt: pending.length > 0 ? 1.5 : 0 }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Approved ({approved.length})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {approved.map((s) => (
                  <RuleCard key={s.id} suggestion={s} onApprove={handleApprove} onReject={handleReject} onEdit={handleEdit} />
                ))}
              </Box>
            </Box>
          )}

          {/* Rejected */}
          {rejected.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.tertiary, fontWeight: 700, mb: 0.75, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Rejected ({rejected.length})
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {rejected.map((s) => (
                  <RuleCard key={s.id} suggestion={s} onApprove={handleApprove} onReject={handleReject} onEdit={handleEdit} />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default RuleSuggestionsPanel;
